import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useMemo } from 'react';

import AutocompleteSingleSelect from '@/ui/components/AutocompleteSingleSelect/AutocompleteSingleSelect';
import Button from '@/ui/components/Button';
import { OptionType } from '@/ui/components/Form/Select/types';
import { convertTo12Hour, convertTo24Hour } from '@/utils/timeFormat';

import type { Props } from './types';

const buildTimeOptions = (): OptionType<string>[] => {
  const options: OptionType<string>[] = [];
  for (let minutes = 0; minutes < 24 * 60; minutes += 15) {
    const hours = Math.floor(minutes / 60)
      .toString()
      .padStart(2, '0');
    const mins = (minutes % 60).toString().padStart(2, '0');
    const label = convertTo12Hour(`${hours}:${mins}`);
    options.push({ value: label, label });
  }
  return options;
};

const normalizeToOption = (
  time: string,
  options: OptionType<string>[]
): OptionType<string> | null => {
  if (!time) return null;
  const label = convertTo12Hour(convertTo24Hour(time));
  return options.find((opt) => opt.value === label) ?? { value: label, label };
};

const TimeIntervalInput: React.FC<Props> = ({ interval, onFromChange, onToChange, onRemove }) => {
  const timeOptions = useMemo(() => buildTimeOptions(), []);
  const emptyOption = useMemo<OptionType<string>>(() => ({ value: '', label: '' }), []);
  const selectedFrom = useMemo(
    () => normalizeToOption(interval.from, timeOptions),
    [interval.from, timeOptions]
  );
  const selectedTo = useMemo(
    () => normalizeToOption(interval.to, timeOptions),
    [interval.to, timeOptions]
  );

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex-1 min-w-[100px]">
          <AutocompleteSingleSelect
            debounce={false}
            options={timeOptions}
            selectedOption={selectedFrom ?? emptyOption}
            onChange={(option) => onFromChange(option?.value ?? '')}
            placeholder="Select time"
            clearable={false}
            label="From"
            showLabel={false}
          />
        </div>
        <span className="text-gray-400 shrink-0">-</span>
        <div className="flex-1 min-w-[100px]">
          <AutocompleteSingleSelect
            debounce={false}
            options={timeOptions}
            selectedOption={selectedTo ?? emptyOption}
            onChange={(option) => onToChange(option?.value ?? '')}
            placeholder="Select time"
            clearable={false}
            label="To"
            showLabel={false}
          />
        </div>
        <Button onClick={onRemove} color="red" ariaLabel="Remove time interval" size="sm">
          <FontAwesomeIcon icon={faTrash} className="text-xs" />
        </Button>
      </div>
    </div>
  );
};

export default React.memo(TimeIntervalInput);
